import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient, JobCreate } from '@/lib/api'
import { storageService } from '@/lib/storage'
import { 
  Target, 
  Settings, 
  FileText, 
  ArrowRight, 
  ArrowLeft,
  Check,
  Plus,
  X,
  Search,
  Filter,
  Users,
  Building,
  MapPin,
  BookOpen,
  Upload,
  Lightbulb,
  Database,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

interface JobWizardProps {}

const steps = [
  { id: 1, title: 'Knowledge Base', icon: BookOpen },
  { id: 2, title: 'Your Requirements', icon: Target },
  { id: 3, title: 'Review & Launch', icon: FileText },
]

const industryOptions = [
  'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education',
  'Real Estate', 'Automotive', 'Energy', 'Telecommunications', 'Media', 'Other'
]

const locationOptions = [
  'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia',
  'Netherlands', 'Sweden', 'Norway', 'Denmark', 'Other'
]

const companySizeOptions = [
  'Startup (1-10)', 'Small (11-50)', 'Medium (51-200)', 'Large (201-1000)', 'Enterprise (1000+)'
]

const promptChips = [
  'CFO tone', 'Exclude vendors', 'Prioritize recent funding', 'Human-verified contacts',
  'Decision makers only', 'Series A+ companies', 'High-growth startups', 'Enterprise focus'
]

export default function JobWizard({}: JobWizardProps) {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [knowledgeBase, setKnowledgeBase] = useState<any[]>([])
  const [activeConnectedSheet, setActiveConnectedSheet] = useState<any>(null)
  const [existingLeads, setExistingLeads] = useState<any[]>([])
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([])
  const [isLoadingKnowledge, setIsLoadingKnowledge] = useState(false)
  const [isLoadingExistingLeads, setIsLoadingExistingLeads] = useState(false)
  const [headerMapping, setHeaderMapping] = useState<any>(null)
  const [formData, setFormData] = useState({
    // Step 1: Targeting
    industry: '',
    location: '',
    companySize: '',
    keywords: [] as string[],
    excludeKeywords: [] as string[],
    
    // Step 2: Quality & Sources
    qualityThreshold: 0.8,
    dataSources: {
      googleSearch: true,
      linkedin: true,
      companyWebsites: true,
      apollo: false,
      clearbit: false,
    },
    verificationLevel: 'standard',
    
    // Step 3: Prompt & Output
    prompt: '',
    selectedChips: [] as string[],
    targetCount: 100,
    outputFormat: 'detailed',
  })

  const [keywordInput, setKeywordInput] = useState('')
  const [excludeKeywordInput, setExcludeKeywordInput] = useState('')

  // Load knowledge base and existing leads on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load knowledge base
        const docs = await storageService.getUploadedDocuments()
        setKnowledgeBase(docs)
        
        // Load active connected sheet
        const activeSheet = await storageService.getActiveConnectedSheet()
        setActiveConnectedSheet(activeSheet)
        
        // Load header mapping for this sheet
        if (activeSheet) {
          const mapping = await storageService.getHeaderMappingBySheetId(activeSheet.id)
          setHeaderMapping(mapping)
        }
        
        // Load existing leads if sheet is connected
        if (activeSheet) {
          setIsLoadingExistingLeads(true)
          try {
            // Use the same user_id as the OAuth flow
            const userId = 'user_123'
            // Extract the actual Google Sheet ID (remove the googlesheets_ prefix)
            const sheetId = activeSheet.id.replace('googlesheets_', '')
            const response = await fetch(`/auth/google/sheets/${sheetId}/read?user_id=${userId}`)
            if (response.ok) {
              const data = await response.json()
              console.log('Sheet data received:', data)
              console.log('Response status:', response.status)
              console.log('Response headers:', response.headers)
              
              // Handle different response formats
              let leads = []
              if (data.success && data.rows) {
                leads = data.rows
                console.log('Using data.rows, count:', leads.length)
              } else if (data.success && data.values) {
                leads = data.values
                console.log('Using data.values, count:', leads.length)
              } else if (data.success && data.data) {
                leads = data.data
                console.log('Using data.data, count:', leads.length)
              } else {
                console.log('No data found in response:', data)
              }
              
              console.log('Processed leads:', leads)
              console.log('First few leads:', leads.slice(0, 3))
              setExistingLeads(leads)
            } else {
              const errorText = await response.text()
              console.error('Failed to load sheet data:', response.status, response.statusText, errorText)
              console.error('Request URL:', `/auth/google/sheets/${sheetId}/read?user_id=${userId}`)
              console.error('Sheet ID:', sheetId)
              console.error('User ID:', userId)
            }
          } catch (error) {
            console.error('Failed to load existing leads:', error)
          } finally {
            setIsLoadingExistingLeads(false)
          }
        }
        
        // Generate suggested prompts based on knowledge base
        generateSuggestedPrompts(docs)
      } catch (error) {
        console.error('Failed to load data:', error)
      }
    }
    
    loadData()
  }, [])

  const generateSuggestedPrompts = (docs: any[]) => {
    const suggestions = []
    
    if (docs.length > 0) {
      suggestions.push("Generate leads as explained in the knowledge base")
      suggestions.push("Find companies matching the target audience profile")
      suggestions.push("Research prospects based on uploaded documents")
    }
    
    // Add generic suggestions
    suggestions.push("Find [industry] companies in [location] with [company size]")
    suggestions.push("Generate leads for [product/service] targeting [audience]")
    suggestions.push("Research decision makers at [company type] companies")
    
    setSuggestedPrompts(suggestions)
  }

  const addKeyword = (keyword: string) => {
    if (keyword.trim() && !formData.keywords.includes(keyword.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keyword.trim()]
      }))
    }
  }

  const removeKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      keywords: prev.keywords.filter(k => k !== keyword)
    }))
  }

  const addExcludeKeyword = (keyword: string) => {
    if (keyword.trim() && !formData.excludeKeywords.includes(keyword.trim())) {
      setFormData(prev => ({
        ...prev,
        excludeKeywords: [...prev.excludeKeywords, keyword.trim()]
      }))
    }
  }

  const removeExcludeKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      excludeKeywords: prev.excludeKeywords.filter(k => k !== keyword)
    }))
  }

  const toggleChip = (chip: string) => {
    setFormData(prev => ({
      ...prev,
      selectedChips: prev.selectedChips.includes(chip)
        ? prev.selectedChips.filter(c => c !== chip)
        : [...prev.selectedChips, chip]
    }))
  }

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleSubmit = async () => {
    try {
      const jobData: JobCreate = {
        prompt: formData.prompt,
        target_count: formData.targetCount,
        quality_threshold: formData.qualityThreshold,
        industry: formData.industry,
        location: formData.location,
        company_size: formData.companySize,
        keywords: formData.keywords,
        exclude_keywords: formData.excludeKeywords,
        data_sources: formData.dataSources,
        verification_level: formData.verificationLevel as 'basic' | 'standard' | 'premium',
        // Add existing leads exclusion
        exclude_existing_leads: existingLeads.length > 0,
        existing_leads: existingLeads,
        connected_sheet_id: activeConnectedSheet?.id,
        header_mapping: headerMapping?.mapping,
        output_format: headerMapping ? 'sheet_mapped' : formData.outputFormat,
        // CRITICAL: Send knowledge base documents to backend
        knowledge_base_documents: knowledgeBase.map(doc => ({
          name: doc.name,
          content: doc.content,
          extractedText: doc.extractedText,
          type: doc.type
        }))
      }
      
      const response = await apiClient.createJob(jobData)
      console.log('Job created successfully:', response)
      
      // Show success message and redirect to job status
      if (response.job_id) {
        navigate(`/jobs/${response.job_id}`)
      } else {
        navigate('/jobs')
      }
    } catch (error) {
      console.error('Failed to create job:', error)
      // Handle error (show toast notification, etc.)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full max-w-6xl mx-auto bg-card rounded-2xl shadow-glow"
      >
        {/* Header */}
        <div className="sticky top-0 bg-card/90 backdrop-blur border-b border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Create New Job</h2>
              <p className="text-muted">Set up your lead generation parameters</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Stepper */}
          <div className="flex items-center gap-3 mt-6">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isActive = currentStep === step.id
              const isCompleted = currentStep > step.id
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "h-8 w-8 rounded-full flex items-center justify-center transition-colors",
                      isActive ? "bg-brand text-white" : 
                      isCompleted ? "bg-green-500 text-white" : "bg-white/10 text-muted"
                    )}>
                      {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </div>
                    <span className={cn(
                      "text-sm font-medium",
                      isActive ? "text-foreground" : "text-muted"
                    )}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-10 h-px bg-white/10" />
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-4">Knowledge Base & Context</h3>
                  
                  {/* Connected Knowledge Base */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="h-5 w-5 text-teal-400" />
                      <h4 className="font-medium">Connected Knowledge Base</h4>
                    </div>
                    
                    {knowledgeBase.length > 0 ? (
                      <div className="space-y-3">
                        {knowledgeBase.map((doc, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 bg-card/50 rounded-lg border border-white/10">
                            <FileText className="h-4 w-4 text-teal-400" />
                            <div className="flex-1">
                              <p className="text-sm font-medium">{doc.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {doc.type} • {new Date(doc.uploadedAt).toLocaleDateString()}
                              </p>
                            </div>
                            <CheckCircle className="h-4 w-4 text-green-400" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 bg-card/30 rounded-lg border border-dashed border-white/20 text-center">
                        <BookOpen className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground mb-2">No knowledge base documents uploaded</p>
                        <p className="text-xs text-muted-foreground">
                          Upload documents in Target Audience Intelligence to enhance lead generation
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Drag and Drop for New Knowledge */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Upload className="h-5 w-5 text-teal-400" />
                      <h4 className="font-medium">Add New Knowledge</h4>
                    </div>
                    <div 
                      className="p-6 bg-card/30 rounded-lg border border-dashed border-white/20 text-center hover:border-teal-400/50 transition-colors cursor-pointer"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault()
                        // Handle file drop
                        console.log('Files dropped:', e.dataTransfer.files)
                      }}
                    >
                      <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground mb-1">Drag & drop files here</p>
                      <p className="text-xs text-muted-foreground">or click to browse</p>
                    </div>
                  </div>

                  {/* Active Connected Sheet */}
                  {activeConnectedSheet && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Database className="h-5 w-5 text-green-400" />
                        <h4 className="font-medium">Connected Sheet</h4>
                      </div>
                      <div className="p-4 bg-card/50 rounded-lg border border-white/10">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-5 w-5 text-green-400" />
                          <div className="flex-1">
                            <p className="font-medium">{activeConnectedSheet.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {existingLeads.length} existing leads will be excluded from search
                            </p>
                          </div>
                        </div>
                        {isLoadingExistingLeads && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            Loading existing leads...
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Suggested Prompts */}
                  {suggestedPrompts.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb className="h-5 w-5 text-yellow-400" />
                        <h4 className="font-medium">Suggested Prompts</h4>
                      </div>
                      <div className="space-y-2">
                        {suggestedPrompts.map((prompt, index) => (
                          <button
                            key={index}
                            onClick={() => setFormData(prev => ({ ...prev, prompt }))}
                            className="w-full p-3 text-left bg-card/30 rounded-lg border border-white/10 hover:border-teal-400/50 transition-colors"
                          >
                            <p className="text-sm">{prompt}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-4">What do you want to target?</h3>
                  <p className="text-muted-foreground mb-6">
                    Describe your ideal prospects in your own words. Be as specific or general as you want.
                  </p>
                  
                  {/* Main Prompt Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Your Targeting Requirements</label>
                    <textarea
                      value={formData.prompt}
                      onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                      placeholder="Describe what you want to target... (e.g., 'Find AI startups in California that have raised Series A funding and are looking for enterprise customers', or 'Wealth management firms in New York with over 50 employees', or 'CFOs at manufacturing companies in the Midwest')"
                      className="w-full rounded-xl bg-white/5 border border-white/10 p-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 min-h-[200px]"
                      rows={8}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Be specific about your target audience, industry, location, company size, funding stage, or any other criteria that matter to you.
                    </p>
                  </div>

                  {/* Smart Suggestions */}
                  {suggestedPrompts.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Lightbulb className="h-5 w-5 text-yellow-400" />
                        <h4 className="font-medium">Smart Suggestions</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {suggestedPrompts.map((prompt, index) => (
                          <button
                            key={index}
                            onClick={() => setFormData(prev => ({ ...prev, prompt }))}
                            className="p-3 text-left bg-card/30 rounded-lg border border-white/10 hover:border-teal-400/50 transition-colors"
                          >
                            <p className="text-sm">{prompt}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Target Count */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">How many leads do you want?</label>
                    <div className="flex items-center gap-4">
                      <Input
                        type="number"
                        value={formData.targetCount}
                        onChange={(e) => setFormData(prev => ({ ...prev, targetCount: parseInt(e.target.value) || 0 }))}
                        placeholder="100"
                        className="w-32"
                      />
                      <span className="text-sm text-muted-foreground">
                        leads to generate
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-4">Review & Launch</h3>
                  
                  {/* Job Summary */}
                  <div className="mb-6">
                    <h4 className="font-medium mb-3">Job Summary</h4>
                    <div className="p-4 bg-card/50 rounded-lg border border-white/10">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Targeting:</span>
                          <span className="text-sm font-medium">{formData.prompt || 'No targeting specified'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Lead Count:</span>
                          <span className="text-sm font-medium">{formData.targetCount} leads</span>
                        </div>
                        {activeConnectedSheet && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Connected Sheet:</span>
                            <span className="text-sm font-medium">{activeConnectedSheet.name}</span>
                          </div>
                        )}
                        {existingLeads.length > 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Existing Leads:</span>
                            <span className="text-sm font-medium">{existingLeads.length} will be excluded</span>
                          </div>
                        )}
                        {existingLeads.length === 0 && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Existing Leads:</span>
                            <span className="text-sm font-medium text-green-400">No existing leads found</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Knowledge Base Summary */}
                  {knowledgeBase.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-3">Knowledge Base</h4>
                      <div className="space-y-2">
                        {knowledgeBase.map((doc, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-card/30 rounded-lg">
                            <FileText className="h-4 w-4 text-teal-400" />
                            <span className="text-sm">{doc.name}</span>
                            <CheckCircle className="h-4 w-4 text-green-400 ml-auto" />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Output Format - Based on Sheet Headers */}
                  {headerMapping && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium mb-2">Output Format (Based on Connected Sheet)</label>
                      <div className="p-4 bg-card/50 rounded-lg border border-white/10">
                        <p className="text-sm text-muted-foreground mb-3">
                          Data will be formatted according to your connected sheet headers:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(headerMapping.mapping).map(([header, field]) => (
                            <div key={header} className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">{header}:</span>
                              <span className="font-medium">{String(field)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fallback Output Format */}
                  {!headerMapping && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium mb-2">Output Format</label>
                      <select
                        value={formData.outputFormat}
                        onChange={(e) => setFormData(prev => ({ ...prev, outputFormat: e.target.value }))}
                        className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50"
                      >
                        <option value="basic">Basic (Name, Company, Email)</option>
                        <option value="detailed">Detailed (Full contact info + company details)</option>
                        <option value="comprehensive">Comprehensive (Full research + outreach suggestions)</option>
                      </select>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-xl font-semibold mb-4">Prompt & Output Configuration</h3>
                  
                  {/* Prompt Chips */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-3">Prompt Enhancements</label>
                    <div className="flex flex-wrap gap-2">
                      {promptChips.map(chip => (
                        <button
                          key={chip}
                          onClick={() => toggleChip(chip)}
                          className={cn(
                            "px-3 py-1 rounded-full text-sm transition-colors",
                            formData.selectedChips.includes(chip)
                              ? "bg-brand text-white"
                              : "bg-white/5 text-muted hover:bg-white/10"
                          )}
                        >
                          {chip}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Main Prompt */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">What do you want to target?</label>
                    <textarea
                      value={formData.prompt}
                      onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                      placeholder="Describe what you want to target... (e.g., 'Find AI startups in California that have raised Series A funding and are looking for enterprise customers')"
                      className="w-full rounded-xl bg-white/5 border border-white/10 p-4 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50 min-h-[120px]"
                      rows={5}
                    />
                    <p className="text-xs text-muted mt-2">
                      Be specific about your target audience, industry, location, and any other criteria.
                    </p>
                  </div>

                  {/* Target Count */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">Target Lead Count</label>
                    <Input
                      type="number"
                      value={formData.targetCount}
                      onChange={(e) => setFormData(prev => ({ ...prev, targetCount: parseInt(e.target.value) || 0 }))}
                      placeholder="100"
                      className="w-32"
                    />
                  </div>

                  {/* Output Format */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Output Format</label>
                    <select
                      value={formData.outputFormat}
                      onChange={(e) => setFormData(prev => ({ ...prev, outputFormat: e.target.value }))}
                      className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/50"
                    >
                      <option value="basic">Basic (Name, Company, Email)</option>
                      <option value="detailed">Detailed (Full contact info + company details)</option>
                      <option value="comprehensive">Comprehensive (Full research + outreach suggestions)</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-card/90 backdrop-blur border-t border-white/10 p-6">
          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={prevStep} disabled={currentStep === 1}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Previous
            </Button>
            
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/jobs')}>
                Cancel
              </Button>
              {currentStep === 3 ? (
                <Button onClick={handleSubmit} className="shadow-glow">
                  <Check className="h-4 w-4 mr-2" />
                  Create Job
                </Button>
              ) : (
                <Button onClick={nextStep} className="shadow-glow">
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
